import { Root, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import { AnimatePresence, MotionProps, motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDebounceValue, useWindowSize } from 'usehooks-ts';
import { ArrowRight } from '../../../../Assets/Icons/ArrowRight';
import { Image } from '../../../../components/Image';
import { EcosystemTabs, ecosystemTabsContent } from './config';
import './styles.css';
import EcosystemBg from './assets/bgMobile.png';

const animationVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 },
};

const animationVariantsImage = {
  hidden: { opacity: 0, x: 10 },
  visible: { opacity: 1, x: 0 },
};

const animationPropsContent: MotionProps = {
  variants: animationVariants,
  initial: 'hidden',
  whileInView: 'visible',
  exit: 'hidden',
  transition: { duration: 0.3 },
  viewport: {
    once: true,
    margin: '-5% 0%',
  },
};

const animationPropsImage: MotionProps = {
  variants: animationVariantsImage,
  initial: 'hidden',
  whileInView: 'visible',
  exit: 'hidden',
  transition: { duration: 0.3 },
  viewport: {
    once: true,
    margin: '-5% 0%',
  },
};

export function Ecosystem() {
  const { width } = useWindowSize();
  const targetRef = useRef(null);
  const isInView = useInView(targetRef, {
    once: true,
    margin: '-30%',
  });
  const [tab, setTab] = useDebounceValue<EcosystemTabs>(
    EcosystemTabs.RetroBridge,
    300
  );

  return (
    <Root
      value={tab}
      onValueChange={tab => setTab(tab as EcosystemTabs)}
      className="main-page--ecosystem"
      ref={targetRef}
    >
      <div className="main-page--ecosystem-bg">
        <img src={EcosystemBg} draggable={false} alt="" />
      </div>
      <motion.h2
        animate={{
          y: isInView ? 0 : 50,
          opacity: isInView ? 1 : 0,
        }}
        transition={{ duration: 0.5 }}
        className="main-page--ecosystem-heading"
      >
        Our Ecosystem
      </motion.h2>
      <TabsList className="main-page--ecosystem-tabs">
        {ecosystemTabsContent.map((item, i) => (
          <TabsTrigger value={item.key} key={item.key} asChild>
            <motion.button
              initial={{ opacity: 0, y: 50 }}
              animate={{
                y: isInView ? 0 : 50,
                opacity: isInView ? 1 : 0,
              }}
              transition={{ duration: 0.5, delay: 0.3 * i }}
              className="main-page--ecosystem-tabs--item"
            >
              <span className="z-1">{item.tabTitle}</span>
            </motion.button>
          </TabsTrigger>
        ))}
      </TabsList>
      <AnimatePresence mode="wait">
        {ecosystemTabsContent.map(
          item =>
            (tab === item.key || width <= 768) && (
              <TabsContent key={item.key} value={item.key} forceMount>
                <div className="row">
                  <div className="d-flex justify-content-center justify-content-md-start align-items-center col-12 col-md-6">
                    <div className="ecosystem-tab--content">
                      <motion.h3
                        {...animationPropsContent}
                        className="ecosystem-tab--content-heading"
                      >
                        {item.heading}
                      </motion.h3>
                      <motion.p
                        {...animationPropsContent}
                        className="ecosystem-tab--content-description"
                      >
                        {item.description}
                      </motion.p>
                      <motion.div
                        className="d-flex justify-content-center justify-content-md-start"
                        {...animationPropsContent}
                      >
                        <Link
                          to={item.actionLink}
                          className="ecosystem-tab--content-action"
                        >
                          {item.actionText}
                          <ArrowRight
                            width={16}
                            height={16}
                            style={{ rotate: '-45deg' }}
                          />
                        </Link>
                      </motion.div>
                    </div>
                  </div>
                  <div className="col">
                    <motion.div
                      {...animationPropsImage}
                      className="ecosystem-tab--image-container"
                    >
                      <Image
                        withSmoothLoading
                        src={item.image}
                        alt=""
                        draggable={false}
                      />
                    </motion.div>
                  </div>
                </div>
              </TabsContent>
            )
        )}
      </AnimatePresence>
    </Root>
  );
}
